import {
  deriveInvestmentSteps,
  formatMilestoneDate,
  InvestmentTransaction,
  InvestmentTrackerStep,
  isInvestmentTransaction,
} from "./InvestmentTracker";
import {
  DetailSummaryRow,
  TransactionDetailLayout,
  TransactionDetailModel,
} from "./TransactionDetailLayout";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function getString(record: Record<string, unknown>, key: string): string {
  return typeof record[key] === "string" ? (record[key] as string).trim() : "";
}

function toFiniteNumber(value: unknown): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatCurrency(value: unknown): string {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatNumber(value: unknown): string {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 8,
  }).format(number);
}

function formatNavps(value: unknown): string {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  return `₱${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(number)}`;
}

function isReinvestment(transaction: InvestmentTransaction): boolean {
  return [
    "reinvestment",
    "dividendReinvestment",
    "dividend-reinvestment",
  ].includes(String(transaction.transactionType));
}

function isFinalized(transaction: InvestmentTransaction): boolean {
  return transaction.status === "approved" || transaction.status === "matured";
}

function hasDeclinedStep(steps: InvestmentTrackerStep[]): boolean {
  return steps.some((step) => step.state === "failed");
}

function buildSummaryRows(
  transaction: InvestmentTransaction,
  finalized: boolean,
  declined: boolean
): DetailSummaryRow[] {
  const fundDetails = asRecord(transaction.fundDetails);
  const fundName = getString(fundDetails, "fundName") || "—";
  const navpsBooked = finalized && !declined;
  const placedAt = navpsBooked
    ? `Placed at ${formatMilestoneDate(transaction.datePlaced)}`
    : undefined;

  return [
    { label: "Fund Name", value: fundName },
    {
      label: "Amount of Investment",
      value: isReinvestment(transaction)
        ? "Not applicable"
        : formatCurrency(transaction.amount),
    },
    {
      label: "NAVPU / Rate at Booking",
      value: declined
        ? "—"
        : navpsBooked
        ? formatNavps(transaction.rateAtBooking)
        : "Pending booking",
      detail: placedAt,
      tone: !navpsBooked || declined ? "muted" : "normal",
    },
    {
      label: "Number of Units",
      value: declined
        ? "—"
        : navpsBooked
        ? formatNumber(transaction.shares)
        : "Pending booking",
      tone: !navpsBooked || declined ? "muted" : "normal",
    },
    {
      label: "Date Processed",
      value: declined
        ? "—"
        : finalized
        ? formatMilestoneDate(transaction.dateProcessed)
        : "In progress",
      tone: !finalized || declined ? "muted" : "normal",
    },
  ];
}

export function deriveInvestmentDetailModel(
  transaction: InvestmentTransaction,
  processingTimeline?: string
): TransactionDetailModel | null {
  if (!isInvestmentTransaction(transaction)) {
    return null;
  }

  const steps = deriveInvestmentSteps(transaction, processingTimeline);
  if (!steps) return null;

  const fundDetails = asRecord(transaction.fundDetails);
  const fundCompanyDetails = asRecord(transaction.fundCompanyDetails);
  const fundName = getString(fundDetails, "fundName") || "Investment";
  const fundCompanyName = getString(fundCompanyDetails, "fundCompanyName");
  const eventId =
    typeof transaction.eventId === "string" ? transaction.eventId : "—";
  const finalized = isFinalized(transaction);
  const declined = hasDeclinedStep(steps);
  const confirmed = finalized && !declined;

  return {
    idPrefix: "investment-detail",
    header: {
      title: fundName,
      metadata: [
        fundCompanyName || "Rampver Investment",
        `Transaction ID ${eventId}`,
      ],
      metricLabel: "Amount Invested",
      metricValue: isReinvestment(transaction)
        ? "Not applicable"
        : formatCurrency(transaction.amount),
    },
    tracker: {
      title: "Investment Tracker",
      ariaLabel: "Investment progress steps",
      description: "Follow your investment from submission to allocation.",
      showChips: false,
      steps,
    },
    summary: {
      title: "Transaction Summary",
      rows: buildSummaryRows(transaction, finalized, declined),
      note:
        !confirmed && !declined
          ? "Final NAVPU and unit figures appear once your order is booked."
          : undefined,
    },
  };
}

interface Props {
  transaction: InvestmentTransaction;
  processingTimeline?: string;
}

export function InvestmentDetailView({
  transaction,
  processingTimeline,
}: Props): JSX.Element | null {
  const model = deriveInvestmentDetailModel(transaction, processingTimeline);
  return model ? <TransactionDetailLayout model={model} /> : null;
}
